// Bausteine für Umzug, Datenauskunft und Import (h-umzug): Status eines
// Auftrags, Fortschritt in Worten, Liste der Exporte mit Download und ein
// Abfrage-Haken, der nachfragt, solange etwas läuft.

import { useCallback, useEffect, useRef, useState } from "react";
import type { UmzugAuftrag } from "../types";
import { Badge, Button } from "./ui";
import { Sym } from "./social-ui";
import { bytesText, datumText, zahlText } from "./hosting-ui";

const STATUS: Record<UmzugAuftrag["status"], { label: string; tone: "neutral" | "info" | "success" | "danger" | "warning" }> = {
  wartet: { label: "wartet", tone: "neutral" },
  laeuft: { label: "läuft", tone: "info" },
  fertig: { label: "fertig", tone: "success" },
  fehler: { label: "Fehler", tone: "danger" },
  kuerzel_noetig: { label: "Kürzel nötig", tone: "warning" },
  abgelaufen: { label: "abgelaufen", tone: "neutral" },
};

export function AuftragStatus({ a }: { a: UmzugAuftrag }): JSX.Element {
  const s = STATUS[a.status] ?? { label: a.status, tone: "neutral" as const };
  return <Badge tone={s.tone} dot={a.status === "laeuft" || a.status === "wartet"}>{s.label}</Badge>;
}

export function laeuftNoch(a: Pick<UmzugAuftrag, "status">): boolean {
  return a.status === "wartet" || a.status === "laeuft";
}

/** Was gerade passiert – in einem Satz. */
export function fortschrittText(a: UmzugAuftrag): string {
  const f = a.fortschritt ?? {};
  switch (f.schritt) {
    case "hochladen": return "Archiv wird hochgeladen …";
    case "pruefen": return "Archiv wird entpackt und geprüft (Prüfsummen) …";
    case "vorpruefung": return "Kennungen, E-Mail-Adressen und Kürzel werden geprüft …";
    case "datenbank":
      return f.tabellen_gesamt
        ? `Datenbank: ${f.tabelle ?? "…"} (${zahlText((f.tabellen_fertig ?? 0) + 1)} von ${zahlText(f.tabellen_gesamt)} Tabellen)`
        : "Datenbank …";
    case "dateien_suchen": return "Dateien werden gesucht …";
    case "dateien":
      return f.dateien_gesamt ? `Dateien: ${zahlText(f.dateien_fertig ?? 0)} von ${zahlText(f.dateien_gesamt)}` : "Dateien werden kopiert …";
    case "start": return "Startet …";
    default: return a.status === "wartet" ? "Wartet – es läuft immer nur ein Auftrag zur Zeit." : "Läuft …";
  }
}

/** Fragt nach, solange ein Auftrag läuft (alle 2 s), sonst nicht. */
export function useAuftraege<T extends { status: UmzugAuftrag["status"] }>(laden: () => Promise<T[]>): {
  liste: T[] | null;
  fehler: string | null;
  neu: () => void;
} {
  const [liste, setListe] = useState<T[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const ladenRef = useRef(laden);
  ladenRef.current = laden;
  const neu = useCallback(() => {
    ladenRef.current().then((l) => { setListe(l); setFehler(null); }).catch((err) => setFehler(err instanceof Error ? err.message : String(err)));
  }, []);
  useEffect(() => { neu(); }, [neu]);
  const aktiv = !!liste?.some(laeuftNoch);
  useEffect(() => {
    if (!aktiv) return;
    const t = window.setInterval(neu, 2000);
    return () => window.clearInterval(t);
  }, [aktiv, neu]);
  return { liste, fehler, neu };
}

/** Liste der Exporte (Umzug oder Datenauskunft) mit Download und Löschen. */
export function ExportListe({ exporte, onDownload, onLoeschen, leer }: {
  exporte: UmzugAuftrag[];
  onDownload: (a: UmzugAuftrag) => void;
  onLoeschen?: (a: UmzugAuftrag) => void;
  leer: string;
}): JSX.Element {
  if (exporte.length === 0) return <p className="text-sm text-muted">{leer}</p>;
  return (
    <ul className="divide-y divide-default rounded-lg border border-default">
      {exporte.map((a) => (
        <li key={a.id} className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <AuftragStatus a={a} />
              <span className="text-sm font-medium text-primary">{datumText(a.created_at, true)}</span>
              {a.optionen?.mitschnitte && <Badge tone="info">mit Mitschnitten</Badge>}
            </div>
            <div className="mt-1 text-xs text-muted">
              {laeuftNoch(a) ? fortschrittText(a)
                : a.status === "fertig" ? <>
                    {bytesText(a.bytes)}
                    {a.ergebnis?.zaehlung && <> · {zahlText(a.ergebnis.zaehlung.zeilen)} Datensätze · {zahlText(a.ergebnis.zaehlung.dateien)} Dateien</>}
                    {a.ablauf_at && <> · verfällt {datumText(a.ablauf_at, true)}</>}
                    {a.erstellt_von_name && <> · von {a.erstellt_von_name}</>}
                  </>
                : a.status === "abgelaufen" ? "Nach Ablauf der Aufbewahrung entfernt."
                : a.fehler ?? "Fehlgeschlagen."}
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            {a.status === "fertig" && (
              <Button size="sm" variant="primary" onClick={() => onDownload(a)}>
                <Sym name="download" /> Herunterladen
              </Button>
            )}
            {onLoeschen && !laeuftNoch(a) && a.status !== "abgelaufen" && (
              <Button size="sm" variant="ghost" onClick={() => onLoeschen(a)} aria-label="Export löschen">
                <Sym name="trash" />
              </Button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Schlichter Balken für Fortschritt (0–1). */
export function Balken({ anteil, label }: { anteil: number; label: string }): JSX.Element {
  const p = Math.max(0, Math.min(1, anteil));
  return (
    <div>
      <div className="h-2 overflow-hidden rounded-full bg-input" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(p * 100)} aria-label={label}>
        <div className="h-full rounded-full bg-brand-primary transition-[width] duration-300" style={{ width: `${p * 100}%` }} />
      </div>
      <div className="mt-1 text-xs tabular-nums text-muted">{label}</div>
    </div>
  );
}
